<template>
    <div
        class="fixed w-screen h-screen top-0 left-0 bg-black/60 z-60 flex items-center justify-center cursor-pointer"
        @keydown.esc="hide"
        @click.self.exact="hide"
    >
        <div class="rounded bg-base-100 shadow-lg cursor-auto w-[50rem] p-3 z-40">
            <sub-account-editor
                ref="newSubAccountEditorRef"
                :sub-account="newSubAccount"
                :fulfillment="fulfillment"
                @created="submit"
                @updated="refresh"
                @deleted="refresh"
                @hide-editor="hide"
            />
        </div>
    </div>
</template>


<script setup lang="ts">

import {nextTick, onMounted, PropType, ref} from 'vue'
import {CUSTOMER, NewSubAccount, SubAccount, SubAccountRelationType} from '@/subaccount/types/SubAccount'
import hotkeys from 'hotkeys-js'
import {fetchCustomers, fetchSuppliers} from '@/subaccount/subAccount.service'
import {usePanelManager} from '@tritt/multipanel'
import {CUSTOMERS, SUPPLIERS} from '@/subaccount'
import emitter from '@/services/infrastructure/emitter'
import {SET_SUB_ACCOUNT_EDITOR_CONTENT} from '@/subaccount/types/SetSubAccountEditorContent'
import SubAccountEditor from '@/subaccount/SubAccountEditor.vue'
import {Address} from '@/subaccount/types/Address'
import {ContactInfo} from '@/subaccount/types/ContactInfo'
import {Fulfillment} from '@/assignments/Assignment.type'

const {setPanelPath} = usePanelManager()
const newSubAccountEditorRef = ref()

const props = defineProps({
    relationType: {
        type: String,
        default: CUSTOMER
    },
    isBusiness: {
        type: Boolean,
        default: true
    },
    name: {
        type: String,
        default: ''
    },
    orgNo: {
        type: String,
        default: ''
    },
    address: {
        type: Object as PropType<Address>,
        default: () => ({
            addressLine: '',
            postalCode: '',
            postalLocation: ''
        })
    },
    contactInfo: {
        type: Object as PropType<ContactInfo>,
        default: () => ({
            name: '',
            email: '',
            phoneNumber: ''
        })
    },
    fulfillment: {
        type: Object as PropType<Fulfillment>
    },
    panelId: {
        type: Number,
        default: undefined
    }
})

const newSubAccount = ref<NewSubAccount>({
    relationType: props.relationType as SubAccountRelationType,
    isBusiness: props.isBusiness,
    orgNo: props.orgNo,
    name: props.name,
    address: props.address,
    contactInfo: props.contactInfo,
    notes: '',
    metadata: {suggestions: {expenseAccount: ''}},
    active: true
})

const showSuccessToast = ref(false)
const emit = defineEmits(['close', 'created'])
const hide = () => {
    emit('close')
}
hotkeys('esc', hide)

const refresh = async () => {
    if (props.relationType === CUSTOMER) {
        await fetchCustomers()
    } else {
        await fetchSuppliers()
    }
    hide()
}

const submit = async (newSacc: SubAccount) => {
    showSuccessToast.value = true
    emit('created', newSacc)
    await refresh()
}

const sendNewSubAccountToPanel = async (panel: number) => {
    hide()
    await setPanelPath({
        componentName: newSubAccount.value.relationType === CUSTOMER ? CUSTOMERS.componentName : SUPPLIERS.componentName,
        panelId: panel
    })

    setTimeout(() => {
        const content = newSubAccount.value
        const fulfillment = props.fulfillment
        emitter.emit(SET_SUB_ACCOUNT_EDITOR_CONTENT, {panelId: panel, content, fulfillment})
    }, 100)
}

onMounted(async () => {
    if(props.panelId){
        await sendNewSubAccountToPanel(props.panelId)
    }
    await nextTick(async () => {
        await newSubAccountEditorRef.value?.focus()
})
})

</script>

<style scoped></style>